import React from "react";
import { Link } from "react-router";
import { Container, Header, Icon, List } from "semantic-ui-react";

function APIGWSelfService() {
  return (
    <Container fluid>
      <Header as="h2" textAlign="center">
        API GW Self-Service
      </Header>
      <Header dividing>Introduction</Header>
      The{" "}
      <Link to="/developer-tools/api-gw-self-service">
        API Gateway Self-Service
      </Link>{" "}
      Tool empowers modules to create and modify API endpoints on AWS API
      Gateway independently, without the need for pull request (PR) approvals.
      This tool streamlines the process of managing API endpoints, enabling
      faster development cycles and greater autonomy for development teams.
      <Header dividing>Features</Header>
      <List as="ul">
        <List.Item as="li">
          <b>Independent Endpoint Management:</b> Create, edit, and delete API
          endpoints without PR approvals.
        </List.Item>
        <List.Item as="li">
          <b>Intuitive User Interface:</b> Easy selection of API types, modules,
          and APIs.
        </List.Item>
        <List.Item as="li">
          <b>Endpoint Details Table:</b> View all endpoints with their details
          and available actions.
        </List.Item>
        <List.Item as="li">
          <b>Frontend Validation:</b> Immediate validation feedback when
          creating or editing endpoints.
        </List.Item>
        <List.Item as="li">
          <b>API Specification Upload:</b> Upload API specification files for
          each endpoint.
        </List.Item>
        <List.Item as="li">
          <b>Automated Pipeline Triggering:</b> Build the SSv3 Jenkins pipeline
          directly from the tool.
        </List.Item>
        <List.Item as="li">
          <b>Error Handling and Feedback:</b> Clear error messages and guidance
          in case of pipeline issues.
        </List.Item>
        <List.Item as="li">
          <b>Jenkins Integration:</b> View pipeline reference numbers and access
          Jenkins logs.
        </List.Item>
      </List>
      <Header dividing>How to Use the API Gateway Self-Service?</Header>
      <List as="ol">
        <List.Item as="li">
          Select the API type by clicking on the REST or WebSocket card.
        </List.Item>
        <List.Item as="li">
          Select the module and the API from the dropdowns.
        </List.Item>
        <List.Item as="li">
          If you want to create a new REST API, click on <i>Create new API</i>{" "}
          button to open the API details form and enter the API details(New
          WebSocket APIs can't be created).
        </List.Item>
        <List.Item as="li">
          A table with the list of endpoints or WebSocket actions along with the
          HTTP method and version will be shown.
        </List.Item>
        <List.Item as="li">
          In the last column, there will three action buttons. You can click on
          them to perform the below operations:
          <List.List as="ol">
            <List.Item as="li">
              View: To view the endpoint details in a read-only modal form. You
              can't edit the details.
            </List.Item>
            <List.Item as="li">
              Edit: To edit the endpoint details and submit them to update them
              using self-service pipeline.
            </List.Item>
            <List.Item as="li">
              Delete: To delete the endpoint using self-service pipeline. You
              need to type delete in the input field to confirm the deletion.
            </List.Item>
          </List.List>
        </List.Item>
        <List.Item as="li">
          Create a new endpoint by clicking on the <i>Create new endpoint</i>{" "}
          button and filling up the endpoint details form.
        </List.Item>
        <List.Item as="li">
          After entering the details in the form, you need to upload the API
          specification file using the file uploader in the API specification
          column. Ensure the file is a valid Swagger or Async API definition.
        </List.Item>
        <List.Item as="li">
          In some cases a single Swagger file should be sufficient for multiple
          endpoints.
          <List.List as="ol">
            <List.Item as="li">
              If all the endpoints are based on lambda integration type and have
              the same lambda arn.
            </List.Item>
            <List.Item as="li">
              If all the endpoints are based on https and have the same base
              path.
            </List.Item>
          </List.List>
        </List.Item>
        <List.Item as="li">
          Once the documentation file is uploaded, click on the build now button
          to build the self-service Jenkins pipeline.
        </List.Item>
        <List.Item as="li">
          If the pipeline is triggered successfully, the build number will be
          shown in the Jenkins response column in the endpoints table. Clicking
          on that will redirect you to the Jenkins build page.
        </List.Item>
        <List.Item as="li">
          The configuration will be shown in the yaml file in the editor below
          the table. If there's an issue building the pipeline. You can copy
          that and use that in the Jenkins UI manually.
        </List.Item>
      </List>
      <Header dividing>Promote endpoints to Higher environments</Header>
      All the endpoints should be created first in dev environment. After
      testing, they should be promoted to SIT and the higher environments. From
      API Portal, we can create a new endpoint only in dev environment. In SIT
      and HE, the list of endpoints which are created in dev will be listed in
      the table. In the action column, there will be a button to promote (
      <Icon name="cloud upload" color="blue" />) the endpoints.
      <br />
      These endpoints are deployed in the previous environment and ready to be
      deployed in the current environment. In the same way, endpoints can be
      deleted only in dev and the endpoints which are deleted in the previous
      environment will be shown in the current environment.
    </Container>
  );
}

export default APIGWSelfService;
